// VIVA-1456 — respaldo de las claves de notificacion de eventos que el esquema
// remoto (`/api/lang/sch`) todavia no publica.
//
// POR QUE EXISTE: los triggers de base de datos (VIVA-1145) escriben la
// notificacion con claves `LBxxxx` en `titulo` y `mensaje`, y `Notification.jsx`
// las resuelve contra el esquema remoto. Cuando la clave no esta en el esquema,
// `parseNotificationContent` pinta la clave cruda, asi que la tarjeta se ve
// "LB1162 @Yair48828627928080" y el usuario no tiene forma de saber que alguien
// reacciono a su evento. Ese es el sintoma que se reporto en VIVA-1456.
//
// MEDIDO EN PRE EL 29/09/2026 contra `/api/lang/sch?ln=es` y `?ln=en` (1388
// claves en los dos idiomas):
//
//   LB1143 / LB0718  REACCION_COMENTARIO    SI existen  -> se ve bien, no se toca
//   LB1157 / LB1158 / LB1159                SI existen  -> ver la nota de abajo
//   LB1160           COMENTARIO_EVENTO      AUSENTE en es y en
//   LB1161           REACCION_EVENTO        AUSENTE en es y en
//   LB1162           REACCION_EVENTO        AUSENTE en es y en
//   LB1163           MODERACION_COMENTARIO  AUSENTE en es y en
//   LB1164           MODERACION_COMENTARIO  AUSENTE en es y en
//
// Coincide con lo anotado en `EventRecordingSection.jsx`: LB1160-LB1170 quedaron
// propuestas por VIVA-1299 y nunca se dieron de alta.
//
// COMO SE RETIRA: el esquema remoto SIEMPRE gana sobre este respaldo (ver
// `resolveNotificationTextKey`). En cuanto backend publique las claves, estos
// textos dejan de usarse solos y el archivo se puede borrar sin tocar nada mas.
//
// DE DONDE SALEN LAS REDACCIONES: no se invento el sentido de ningun mensaje. Se
// calcaron de las claves hermanas que el esquema SI publica para el caso de
// comentarios, cambiando unicamente el sustantivo:
//
//   LB1143 "Reaccionó a tu comentario"  -> LB1161 "Reaccionó a tu evento"
//   LB0718 " reaccionó a tu comentario." -> LB1162 " reaccionó a tu evento."
//
// LB1160, LB1163 y LB1164 no tienen hermana exacta. El mensaje llega como
// `LBxxxx,@usuario[,<texto>]`, o sea que la clave es el verbo que antecede al
// texto. Se redactaron en ese molde, y son las mas probables de corregir cuando
// backend publique las suyas.
//
// OJO, DEFECTO DE ORIGEN QUE NO SE ARREGLA AQUI: algunas filas de
// COMENTARIO_EVENTO llegan con `titulo: LB1157` y `mensaje: LB1159,...`. Esas dos
// claves SI existen y dicen "Reaccionó" y "a su propio mensaje", asi que la
// tarjeta se lee "Reaccionó a su propio mensaje" para lo que en realidad es un
// comentario en un evento. Es el trigger el que manda las claves equivocadas; el
// front pinta lo que recibe y no hay forma de distinguirlo desde aqui sin
// sobrescribir texto que el esquema si publica.

export const EVENT_NOTIFICATION_FALLBACK_TEXTS = {
  es: {
    LB1161: "Reaccionó a tu evento",
    LB1162: " reaccionó a tu evento.",
    LB1160: " comentó en tu evento:",
    LB1163: "Comentario eliminado",
    LB1164: " eliminó tu comentario por moderación.",
  },
  en: {
    LB1161: "Reacted to your event",
    LB1162: " reacted to your event.",
    LB1160: " commented on your event:",
    LB1163: "Comment removed",
    LB1164: " removed your comment by moderation.",
  },
};

/**
 * Resuelve una clave `LBxxxx` de notificacion contra el esquema remoto y, solo
 * si ahi no esta, contra el respaldo local.
 *
 * El orden importa: el esquema remoto gana siempre, de modo que el dia que
 * backend publique la clave el respaldo se apaga sin que nadie tenga que
 * acordarse de venir a borrarlo.
 *
 * @param {string} key clave del esquema, por ejemplo "LB1162"
 * @param {object} remoteTexts diccionario de `UiTextsContext`
 * @param {boolean} isSpanish idioma activo de la interfaz
 * @returns {string|null} el texto a pintar, o null si no hay ninguno
 */
export const resolveNotificationTextKey = (key, remoteTexts, isSpanish) => {
  if (!key) return null;

  const remoteText = remoteTexts?.[key];
  if (remoteText) return remoteText;

  const fallbacks =
    EVENT_NOTIFICATION_FALLBACK_TEXTS[isSpanish ? "es" : "en"] ?? {};

  return fallbacks[key] ?? null;
};
